import type { Assessment, Answers, Lead, SubmissionScores, RegistrationStatus } from './types';
import { getVisitorId, trackableVisitorId, type AttributionPayload } from './attribution';

const API = import.meta.env.VITE_API_BASE_URL as string;
if (!API) {
  // Fail loudly in dev if the env var isn't wired up.
  // eslint-disable-next-line no-console
  console.warn('VITE_API_BASE_URL is not set — API calls will fail.');
}

/**
 * Fetch with one-shot retry on transient network failures.
 *
 * `fetch()` rejects (TypeError "Failed to fetch" / "Load failed") when the
 * request can't reach the server at all — DNS blip, TLS handshake drop,
 * Wi-Fi flap, intermediate proxy reset. These are very different from a
 * worker that responded with a 4xx/5xx (in that case fetch resolves and
 * `res.ok` is false; we DO NOT retry there because the worker already saw
 * the request and may have side-effected).
 *
 * Lisa hit this 2026-05-06 on her CBL survey-complete PATCH: the first
 * attempt rejected with "Failed to fetch" (no worker log), her second
 * attempt 5 seconds later went through cleanly. Auto-retrying once with
 * a brief delay would have been invisible to her.
 *
 * Used for the user's "I just spent time on this form, don't make me redo
 * it" moments — submitAssessment and patchAnswers — and for the page's own
 * first load, fetchAssessment (a GET, so a repeat has no side effect).
 * Other fetches are cheap to retry from the UI.
 *
 * fetchAssessment joined 2026-10-14 (mct_d7b8dd34-7db): a paid Facebook
 * visitor on summit-2026-na (13 Oct, iPhone, Facebook's in-app browser) got
 * the "Something didn't work right" page because this one GET dropped, while
 * the landing-view and ticket POSTs from the same phone reached the worker in
 * the same second and the worker logged no error. A dead page on first load
 * is not cheap to retry from the UI — it loses the ad click.
 */
async function fetchWithNetworkRetry(input: RequestInfo, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    // TypeError is what fetch throws on network-level failure. Anything else
    // (e.g. DOMException from AbortController) we surface immediately.
    if (!(err instanceof TypeError)) throw err;
    // eslint-disable-next-line no-console
    console.warn('[api] network error, retrying once in 1s:', err.message);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return fetch(input, init);
  }
}

export async function fetchAssessment(slug: string): Promise<Assessment> {
  // Forward `?phase=` from the page URL so admin previews can render any phase
  // (pre-event / replay / archived) of a registration-type LP from a single URL.
  // Worker validates the value; bogus values are ignored server-side.
  const pageParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const phase = pageParams.get('phase');
  const qs = phase ? `?phase=${encodeURIComponent(phase)}` : '';
  const res = await fetchWithNetworkRetry(`${API}/api/assessments/${slug}${qs}`);
  if (!res.ok) {
    throw new Error(`Assessment not found: ${slug} (${res.status})`);
  }
  const data = await res.json();
  // Worker returns { assessment: {...} } — unwrap if present.
  return (data.assessment ?? data) as Assessment;
}

export interface SubmitPayload {
  assessmentSlug: string;
  lead: Lead;
  answers: Answers;
  attribution?: AttributionPayload;
}

export interface SubmitResponse {
  submissionId: string;
  scores: SubmissionScores;
}

export interface FetchSubmissionResponse {
  submissionId: string;
  assessment: Assessment;
  scores: SubmissionScores;
  answers: Answers;
  resultKey: string;
}

export async function fetchSubmission(id: string): Promise<FetchSubmissionResponse> {
  const res = await fetch(`${API}/api/submissions/${encodeURIComponent(id)}`);
  if (!res.ok) {
    throw new Error(`Submission not found (${res.status})`);
  }
  const data = await res.json();
  const submission = data.submission;
  return {
    submissionId: submission.id,
    assessment: data.assessment as Assessment,
    scores: submission.scores as SubmissionScores,
    answers: (submission.answers ?? {}) as Answers,
    resultKey: submission.resultKey,
  };
}

export async function submitAssessment(payload: SubmitPayload): Promise<SubmitResponse> {
  const body = {
    assessmentSlug: payload.assessmentSlug,
    firstName: payload.lead.firstName,
    lastName: payload.lead.lastName,
    email: payload.lead.email,
    country: payload.lead.country,
    phone: payload.lead.phone,
    company: payload.lead.company,
    consent: payload.lead.consent,
    answers: payload.answers,
    attribution: payload.attribution,
  };
  const res = await fetchWithNetworkRetry(`${API}/api/submissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Submission failed (${res.status}): ${txt}`);
  }
  const data = await res.json();
  // Ensure scores.result is populated — worker returns resultKey separately
  const scores = data.scores as SubmissionScores;
  if (!scores.result && data.resultKey) {
    scores.result = data.resultKey;
  }
  return {
    submissionId: data.submissionId ?? data.submission?.id ?? data.id,
    scores,
  };
}

// ---------- Embed resume / survey-complete / opt-out ----------

export interface ResumeResponse {
  submissionId: string;
  assessmentSlug: string;
  email: string;
  firstName: string | null;
  status: RegistrationStatus | null;
  skipEmailGate: boolean;
  /** True when submissions.tier_key = 'vip' — member auto-VIP, and also a
   *  VIP buyer (the purchase webhook writes the same row). */
  memberVip?: boolean;
  /** The submission's tier_key (null = free / none) and where it came from
   *  ('purchase' | 'membership' | 'referral' | 'free'). A Spiffy purchase
   *  writes both onto this same row. Drives the upgrade step and the
   *  thank-you's "pass confirmed" line (worker/src/resume-tier.ts). */
  tierKey?: string | null;
  tierSource?: string | null;
}

export async function resumeFromToken(token: string): Promise<ResumeResponse> {
  const res = await fetch(`${API}/api/embed/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Resume failed (${res.status}): ${txt}`);
  }
  return res.json();
}

/** PATCH answers onto an existing submission (the registration was already created by
 *  the widget). Re-scores, transitions status to survey-complete, cancels survey-track
 *  reminders, fires answer-dependent notifications. */
export async function patchAnswers(submissionId: string, answers: Answers, attribution?: AttributionPayload): Promise<SubmitResponse> {
  const res = await fetchWithNetworkRetry(`${API}/api/submissions/${encodeURIComponent(submissionId)}/answers`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers, attribution }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Answers patch failed (${res.status}): ${txt}`);
  }
  const data = await res.json();
  const scores = data.scores as SubmissionScores;
  if (!scores.result && data.resultKey) scores.result = data.resultKey;
  return { submissionId: data.submissionId ?? submissionId, scores };
}

export async function optOutSurvey(submissionId: string): Promise<{ ok: boolean; status: RegistrationStatus }> {
  const res = await fetch(`${API}/api/submissions/${encodeURIComponent(submissionId)}/opt-out-survey`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Opt-out failed (${res.status}): ${txt}`);
  }
  return res.json();
}

/** Fire-and-forget landing-views beacon. Engine-hosted landings (taker SPA)
 *  call this on mount so the funnel can compute visitor → email-submitted
 *  conversion. Bot UAs are dropped server-side. Failures are silent. */
export function recordLandingView(slug: string): void {
  if (!slug || typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const body = {
      landingHost: url.host || null,
      landingPath: url.pathname || null,
      utmSource: params.get('utm_source'),
      utmMedium: params.get('utm_medium'),
      utmCampaign: params.get('utm_campaign'),
      utmContent: params.get('utm_content'),
      utmTerm: params.get('utm_term'),
      attribution: params.get('att'),
      // Stitches this anonymous pageview to a later opt-in (submission_attribution
      // stores the same id) — without it the funnel can't dedupe repeat views either.
      visitorId: getVisitorId(),
      // A campaign email link's signed per-reader reference (worker
      // email-link-ref.ts): the worker ties this browser to the reader. Never
      // sent for a browser that opted out of tracking.
      emailRef: trackableVisitorId() ? params.get('ee_r') : null,
    };
    const json = JSON.stringify(body);
    const target = `${API}/api/landing-views/${encodeURIComponent(slug)}`;
    // The reference is for this one visit. Take it off the address so a link
    // copied from the address bar and shared does not carry it.
    if (params.has('ee_r')) {
      params.delete('ee_r');
      window.history.replaceState(window.history.state, '', url.pathname + (params.toString() ? `?${params}` : '') + url.hash);
    }
    // sendBeacon survives unload + ignores response; ideal for tracking.
    if ('sendBeacon' in navigator) {
      const blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(target, blob);
      return;
    }
    void fetch(target, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never throw from a tracking call
  }
}

// ---------- Referrals: the registrant's own link (thank-you page) ----------

export interface RegistrantReferral {
  shortUrl: string;
  count: number;
  nextTreat: { atReferrals: number; label: string; more: number } | null;
  /** Their own share page (/share/<token>), when the worker has one. */
  sharePageUrl?: string | null;
}

/** A referrer's own share page (/share/:token, job mct_ee41e1ee-080). */
export interface SharePageData {
  firstName: string | null;
  eventTitle: string;
  shortUrl: string;
  count: number;
  nextTreat: { atReferrals: number; label: string; more: number } | null;
  shareText: string;
  emailSubject: string;
  targets: SharePageTargets;
  /** Every ready-made post (job mct_5b9caba4-a4d): a LinkedIn version and a
   *  short X/WhatsApp one, with share addresses and copy text the worker built
   *  around the referrer's link. Absent only from a worker older than posts. */
  posts?: SharePagePost[];
  images: Array<{ url: string; label: string; kind: string | null }>;
  /** "Make your card" (job mct_7e138a6a-e47): the card this referrer is
   *  offered, decided by the worker. Absent or null: no cards section. */
  cards?: ShareCards | null;
}

export type ShareCardRole = 'speaking' | 'attending' | 'promoting';
export interface ShareCardTextSlot {
  x: number; y: number; w: number; align: 'left' | 'center' | 'right';
  maxPx: number; minPx: number; weight: number; colour: string;
}
export interface ShareCardVariant {
  role: ShareCardRole;
  photo: boolean;
  size: 'square' | 'portrait' | 'story' | 'landscape' | 'email';
  width: number;
  height: number;
  background: string;
  slots?: { photo: { cx: number; cy: number; r: number; ring: number }; name: ShareCardTextSlot; firm: ShareCardTextSlot };
}
export interface ShareCards {
  version: string;
  role: ShareCardRole;
  withPhoto: ShareCardVariant[];
  noPhoto: ShareCardVariant[];
  name: string;
  firm: string;
  /** The speaker's lineup headshot is ready at fetchShareCardPhoto. */
  photo: boolean;
  /** The person's own saved photo (job mct_f290e99a-69f), ready at
   *  fetchSavedSharePhoto, with the crop they set. Absent or null: none. */
  savedPhoto?: SavedSharePhoto | null;
  limits: { name: number; firm: number };
}
export interface SavedSharePhoto { crop: { zoom: number; x: number; y: number }; width: number; height: number; version: string }

const myPhotoUrl = (token: string) => `${API}/api/public/referrals/share/${encodeURIComponent(token)}/my-photo`;

/** The person's own saved card photo. null when there is none or it does
 *  not load. */
export async function fetchSavedSharePhoto(token: string): Promise<Blob | null> {
  try {
    const res = await fetch(myPhotoUrl(token), { cache: 'no-store' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.size > 0 && blob.type.startsWith('image/') ? blob : null;
  } catch {
    return null;
  }
}

/** Keep (or replace) the person's card photo, a JPEG the page has already
 *  scaled. Never throws: the error is the worker's plain sentence. */
export async function saveSharePhoto(token: string, jpeg: Blob, crop: { zoom: number; x: number; y: number }):
  Promise<{ ok: true; savedPhoto: SavedSharePhoto } | { ok: false; error: string }> {
  try {
    const q = new URLSearchParams({ zoom: String(crop.zoom), x: String(crop.x), y: String(crop.y) });
    const res = await fetch(`${myPhotoUrl(token)}?${q}`, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: jpeg });
    const d = await res.json().catch(() => null) as { savedPhoto?: SavedSharePhoto; error?: string } | null;
    if (res.ok && d?.savedPhoto) return { ok: true, savedPhoto: d.savedPhoto };
    return { ok: false, error: d?.error ?? 'Your photo could not be saved just now.' };
  } catch {
    return { ok: false, error: 'Your photo could not be saved just now.' };
  }
}

/** Save a new crop for the saved photo. true when it saved. */
export async function saveSharePhotoCrop(token: string, crop: { zoom: number; x: number; y: number }): Promise<boolean> {
  try {
    const res = await fetch(myPhotoUrl(token), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(crop) });
    return res.ok;
  } catch {
    return false;
  }
}

/** Delete the saved photo (the object and the record). true when gone. */
export async function removeSavedSharePhoto(token: string): Promise<boolean> {
  try {
    const res = await fetch(myPhotoUrl(token), { method: 'DELETE' });
    return res.ok;
  } catch {
    return false;
  }
}

/** A speaker's own lineup headshot, through the worker so the card canvas
 *  can export it. null when there is none or it does not load. */
export async function fetchShareCardPhoto(token: string): Promise<Blob | null> {
  try {
    const res = await fetch(`${API}/api/public/referrals/share/${encodeURIComponent(token)}/photo`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.size > 0 && blob.type.startsWith('image/') ? blob : null;
  } catch {
    return null;
  }
}

/** Count a card made, downloaded or shared against the promotion (a plain
 *  total, nobody named). Never throws, never waits on the answer. */
export function postShareCardEvent(token: string, action: 'made' | 'downloaded' | 'shared', role: ShareCardRole): void {
  try {
    void fetch(`${API}/api/public/referrals/share/${encodeURIComponent(token)}/card-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, role }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* counting never gets in the way */ }
}

export interface SharePageTargets { linkedin: string; facebook: string; x: string; whatsapp: string; email: string }

export interface SharePagePost {
  label: string;
  linkedin: string;
  short: string;
  targets: SharePageTargets;
  copy: { linkedin: string; short: string };
}

/** null for an unknown token or any failure; never throws. */
export async function fetchSharePage(token: string): Promise<SharePageData | null> {
  try {
    const res = await fetch(`${API}/api/public/referrals/share/${encodeURIComponent(token)}`);
    if (!res.ok) return null;
    const d = await res.json() as { share?: SharePageData | null };
    return d.share ?? null;
  } catch {
    return null;
  }
}

/** The registrant's own referral link and how many registered through it.
 *  null whenever the event has no registrant referral campaign; never throws
 *  (the block simply does not show). */
export async function fetchRegistrantReferral(submissionId: string): Promise<RegistrantReferral | null> {
  try {
    const res = await fetch(`${API}/api/public/referrals/registrant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ submissionId }),
    });
    if (!res.ok) return null;
    const d = await res.json() as { referral?: RegistrantReferral | null };
    return d.referral ?? null;
  } catch {
    return null;
  }
}
